import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { UserInterface } from '@sport-activities/sdk';
import { OAuthSuccessEvent } from 'angular-oauth2-oidc';
import { from, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { APP_STORAGE } from '~/app.token';
import { LoginState } from '~/auth/interface/login-state.interface';
import { AbstractAuthProvider } from '~/auth/providers/abstract-auth.provider';
import { ConnectProvider } from '~/auth/providers/connect.provider';
import { FedProvider } from '~/auth/providers/fed.provider';
import { UserService } from '~/auth/services/user/user.service';
import { ACTIVITIES_BASE_URL } from '~/core/core.module';
import {
  ParamethorContextType,
  ParamethorService,
} from '~/paramethor/paramethor.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private readonly paramethorService: ParamethorService,
    private readonly fedProvider: FedProvider,
    private readonly connectProvider: ConnectProvider,
    private readonly userService: UserService,
    private readonly httpClient: HttpClient,
    @Inject(ACTIVITIES_BASE_URL) private readonly activitiesUrl: string,
    @Inject(APP_STORAGE) private readonly storage: Storage
  ) {}

  public getCurrentAuthProvider(): string {
    // issuer from storage
    return this.storage.getItem('auth_iss');
  }

  /**
   * get an auth provider by issuer
   */
  public getProviderByIssuer(issuer: string): AbstractAuthProvider {
    switch (issuer) {
      case this.fedProvider.getIssuer():
        return this.fedProvider;
      case this.connectProvider.getIssuer():
        return this.connectProvider;
      default:
        throw new Error(`No provider found for issuer ${issuer}`);
    }
  }

  public getUserProfile(): Observable<UserInterface> {
    return this.httpClient.get<UserInterface>(`${this.activitiesUrl}/me`).pipe(
      map((user) => {
        this.userService.setUser(user);
        this.paramethorService
          .load(ParamethorContextType.AUTH_PROVIDER, user.providerName)
          .subscribe();

        const authIssuer = this.getCurrentAuthProvider();
        if (authIssuer) {
          const oAuthService =
            this.getProviderByIssuer(authIssuer).getOAuthService();
          this.userService
            .updateUserTokens(
              oAuthService.getAccessToken(),
              oAuthService.getRefreshToken()
            )
            .subscribe();
        }
        return user;
      })
    );
  }

  public initialize(): Observable<UserInterface> {
    const currentAuthProviderIssuer = this.getCurrentAuthProvider();
    const currentProvider = [this.fedProvider, this.connectProvider].find(
      (provider) => provider.getIssuer() === currentAuthProviderIssuer
    );

    if (!currentProvider) {
      return of(undefined);
    }

    return from(currentProvider.configure()).pipe(
      mergeMap(() => currentProvider.tryLogin()),
      mergeMap((isLogged) => (isLogged ? this.getUserProfile() : of(undefined)))
    );
  }
  /**
   * Check if user is logged
   */
  public isAuthenticated(): boolean {
    // issuer from storage
    const authIssuer = this.getCurrentAuthProvider();

    // no issuer, no login
    if (!authIssuer) {
      return false;
    }

    // retrieve matching provider isAuthenticated
    return this.getProviderByIssuer(authIssuer).isAuthenticated();
  }

  /**
   * login by issuer
   */
  public login(
    issuer: string,
    additionalState?: LoginState
  ): Promise<OAuthSuccessEvent> {
    return this.getProviderByIssuer(issuer).login(additionalState);
  }

  /**
   * logout user
   */
  public logout(): Promise<OAuthSuccessEvent> {
    // issuer from storage
    const authIssuer = this.getCurrentAuthProvider();

    // no issuer, no login
    if (!authIssuer) {
      return Promise.reject(new Error('No provider found for issuer'));
    }

    return this.getProviderByIssuer(authIssuer).logout();
  }

  public resetIssuer(): void {
    return this.storage.removeItem('auth_iss');
  }
}
